import { type Infer, v } from 'convex/values'

import { categoryFromTags } from './habitCategory'

// Movement patterns for session-plan items: drives the animated figure on each
// exercise card (lib/moveFigures). Matchers cover en, ja, es, pt, fr, zh.

export const STRENGTH_MOVES = [
  'pushup',
  'squat',
  'lunge',
  'plank',
  'bench',
  'overhead',
  'hinge',
  'row',
  'pullup',
  'curl',
  'bridge',
  'crunch',
  'jumpingjack',
  'burpee',
  'climber',
  'run',
  'cycle',
  'stretch',
] as const

export const YOGA_MOVES = [
  'downdog',
  'cobra',
  'child',
  'catcow',
  'warrior1',
  'warrior2',
  'tree',
  'triangle',
  'chair',
  'bridgepose',
  'forwardfold',
  'lowlunge',
  'mountain',
  'pose',
] as const

export const MOVE_IDS = [...STRENGTH_MOVES, ...YOGA_MOVES, 'generic'] as const
export type MoveId = (typeof MOVE_IDS)[number]

export const moveValidator = v.union(...MOVE_IDS.map((m) => v.literal(m)))
export type MoveValue = Infer<typeof moveValidator>

export const isMoveId = (s: unknown): s is MoveId =>
  typeof s === 'string' && (MOVE_IDS as readonly string[]).includes(s)

// Latin words match at a word start (so "run" hits "running" but not "brunch");
// CJK and kana match anywhere.
function matcher(words: readonly string[]) {
  const latin: string[] = []
  const cjk: string[] = []
  for (const w of words) (/^[\x20-\u024f'’-]+$/.test(w) ? latin : cjk).push(w)
  const esc = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/[\s-]+/g, '[\\s-]?')
  const re = latin.length
    ? new RegExp(`(?:^|[^a-z\\u00c0-\\u024f])(?:${latin.map(esc).join('|')})`, 'i')
    : null
  return (text: string) => {
    const s = text.toLowerCase()
    return (!!re && re.test(s)) || cjk.some((w) => s.includes(w.toLowerCase()))
  }
}

const YOGA_WORDS = [
  // en
  'yoga',
  'stretch',
  'mobility',
  'flexibility',
  'vinyasa',
  'asana',
  // es
  'estira',
  'estiramiento',
  'movilidad',
  'flexibilidad',
  // pt
  'ioga',
  'alonga',
  'mobilidade',
  'flexibilidade',
  // fr
  'étire',
  'étirement',
  'etirement',
  "s'étirer",
  'mobilité',
  'souplesse',
  // ja
  'ヨガ',
  'ストレッチ',
  '柔軟',
  'モビリティ',
  // zh
  '瑜伽',
  '拉伸',
  '伸展',
  '柔韧',
  '灵活性',
]

const EXERCISE_WORDS = [
  ...YOGA_WORDS,
  // en
  'gym',
  'workout',
  'work out',
  'run',
  'jog',
  'lift',
  'weights',
  'weightlifting',
  'push-up',
  'pushup',
  'press-up',
  'pressup',
  'squat',
  'plank',
  'pilates',
  'swim',
  'cycle',
  'cycling',
  'bike',
  'biking',
  'hiit',
  'cardio',
  'training',
  'exercise',
  'sit-up',
  'situp',
  'burpee',
  'strength',
  'fitness',
  'crossfit',
  // es
  'gimnasio',
  'entrena',
  'entrenamiento',
  'ejercicio',
  'correr',
  'trotar',
  'pesas',
  'flexiones',
  'sentadilla',
  'plancha',
  'nadar',
  'natación',
  'bici',
  'ciclismo',
  'abdominales',
  // pt
  'academia',
  'malhar',
  'malhação',
  'treino',
  'treinar',
  'exercício',
  'exercicio',
  'corrida',
  'musculação',
  'flexões',
  'flexão',
  'agachamento',
  'prancha',
  'natação',
  'bicicleta',
  'pedalar',
  'abdominais',
  // fr
  'salle de sport',
  'muscu',
  'entraînement',
  'entrainement',
  "s'entraîner",
  'exercice',
  'courir',
  'course à pied',
  'footing',
  'jogging',
  'haltères',
  'pompes',
  'gainage',
  'planche',
  'nager',
  'natation',
  'vélo',
  'cyclisme',
  'abdos',
  // ja
  'ジム',
  '筋トレ',
  'トレーニング',
  '運動',
  'ワークアウト',
  'ランニング',
  'ジョギング',
  '走る',
  'ウエイト',
  '腕立て',
  'スクワット',
  'プランク',
  'ピラティス',
  '水泳',
  '泳ぐ',
  'サイクリング',
  '自転車',
  '有酸素',
  '腹筋',
  'バーピー',
  // zh
  '健身',
  '锻炼',
  '运动',
  '训练',
  '跑步',
  '慢跑',
  '举重',
  '力量',
  '俯卧撑',
  '深蹲',
  '平板支撑',
  '普拉提',
  '游泳',
  '骑行',
  '自行车',
  '有氧',
  '仰卧起坐',
  '波比',
]

const isExerciseTitle = matcher(EXERCISE_WORDS)
export const isYogaTitle = matcher(YOGA_WORDS)

// The "Build a plan" chip: a Health habit whose title reads like exercise.
export function looksLikeExercise(title: string, category?: string | null) {
  if (!category || categoryFromTags([category]) !== 'health') return false
  return isExerciseTitle(title.split('///')[0] ?? title)
}

// Ordered: the first pattern whose words match wins, so specific names come
// before the general ones they contain ("mountain climber" before "mountain").
const MOVE_WORDS: [MoveId, string[]][] = [
  [
    'downdog',
    [
      'downward dog',
      'downward-facing',
      'down dog',
      'adho mukha',
      'perro boca abajo',
      'cachorro olhando para baixo',
      'cão olhando para baixo',
      'chien tête en bas',
      '下向きの犬',
      'ダウンドッグ',
      '下犬式',
    ],
  ],
  [
    'cobra',
    [
      'cobra',
      'upward dog',
      'upward-facing',
      'up dog',
      'bhujangasana',
      'perro boca arriba',
      'cachorro olhando para cima',
      'cão olhando para cima',
      'chien tête en haut',
      'コブラ',
      '上向きの犬',
      '眼镜蛇',
      '上犬式',
    ],
  ],
  [
    'child',
    [
      "child's pose",
      'childs pose',
      'child pose',
      'balasana',
      'postura del niño',
      'postura da criança',
      "posture de l'enfant",
      "l'enfant",
      '子供のポーズ',
      'チャイルド',
      '婴儿式',
      '儿童式',
    ],
  ],
  [
    'catcow',
    [
      'cat-cow',
      'cat cow',
      'gato-vaca',
      'gato vaca',
      'chat-vache',
      'chat vache',
      'キャットカウ',
      '猫牛',
      '猫式',
    ],
  ],
  [
    'warrior2',
    [
      'warrior 2',
      'warrior ii',
      'virabhadrasana 2',
      'virabhadrasana ii',
      'guerrero 2',
      'guerrero ii',
      'guerreiro 2',
      'guerreiro ii',
      'guerrier 2',
      'guerrier ii',
      '戦士のポーズ2',
      '戦士のポーズii',
      '戦士2',
      '战士二',
      '战士2',
    ],
  ],
  ['warrior1', ['warrior', 'virabhadrasana', 'guerrero', 'guerreiro', 'guerrier', '戦士', '战士']],
  [
    'tree',
    [
      'tree',
      'vrksasana',
      'árbol',
      'arbol',
      'árvore',
      'arvore',
      'arbre',
      '木のポーズ',
      '立木',
      '树式',
    ],
  ],
  ['triangle', ['triangle', 'trikonasana', 'triángulo', 'triangulo', 'triângulo', '三角']],
  [
    'chair',
    [
      'chair pose',
      'utkatasana',
      'postura de la silla',
      'silla',
      'cadeira',
      'chaise',
      '椅子のポーズ',
      '椅子',
      '幻椅',
    ],
  ],
  [
    'lowlunge',
    [
      'low lunge',
      'crescent',
      'anjaneyasana',
      'estocada baja',
      'avanço baixo',
      'fente basse',
      'ローランジ',
      '低弓步',
      '新月',
    ],
  ],
  [
    'forwardfold',
    [
      'forward fold',
      'forward bend',
      'uttanasana',
      'paschimottanasana',
      'seated fold',
      'pinza',
      'flexión hacia delante',
      'flexão para frente',
      'pince',
      'flexion avant',
      '前屈',
    ],
  ],
  [
    'pushup',
    [
      'push-up',
      'push up',
      'pushup',
      'press-up',
      'press up',
      'flexiones',
      'flexão',
      'flexões',
      'pompes',
      '腕立て',
      'プッシュアップ',
      '俯卧撑',
    ],
  ],
  [
    'bench',
    [
      'bench',
      'chest press',
      'press de banca',
      'supino',
      'développé couché',
      'developpe couche',
      'ベンチプレス',
      '卧推',
    ],
  ],
  [
    'overhead',
    [
      'overhead',
      'shoulder press',
      'military press',
      'press militar',
      'press de hombros',
      'desenvolvimento',
      'développé militaire',
      'ショルダープレス',
      '推举',
      '肩推',
    ],
  ],
  [
    'hinge',
    [
      'deadlift',
      'romanian',
      'rdl',
      'hinge',
      'good morning',
      'kettlebell swing',
      'swing',
      'peso muerto',
      'levantamento terra',
      'stiff',
      'soulevé de terre',
      'souleve de terre',
      'デッドリフト',
      '硬拉',
    ],
  ],
  [
    'pullup',
    [
      'pull-up',
      'pull up',
      'pullup',
      'chin-up',
      'chin up',
      'chinup',
      'pulldown',
      'pull-down',
      'dominada',
      'barra fixa',
      'puxada',
      'traction',
      '懸垂',
      'チンニング',
      'ラットプル',
      '引体向上',
      '下拉',
    ],
  ],
  ['row', ['row', 'remo', 'rowing', 'rameur', 'tirage', 'ロウ', 'ローイング', '划船']],
  ['curl', ['curl', 'bicep', 'bíceps', 'カール', '弯举', '二头']],
  [
    'climber',
    [
      'mountain climber',
      'climber',
      'escalador',
      'alpinista',
      'grimpeur',
      'マウンテンクライマー',
      '登山跑',
      '登山者',
    ],
  ],
  [
    'jumpingjack',
    [
      'jumping jack',
      'star jump',
      'saltos de tijera',
      'tijeras',
      'polichinelo',
      'jumping-jack',
      'ジャンピングジャック',
      '开合跳',
    ],
  ],
  ['burpee', ['burpee', 'バーピー', '波比']],
  [
    'bridge',
    [
      'glute bridge',
      'hip thrust',
      'hip raise',
      'bridge',
      'setu bandha',
      'puente',
      'elevação pélvica',
      'ponte',
      'pont',
      'ヒップリフト',
      'ブリッジ',
      '橋のポーズ',
      '臀桥',
      '桥式',
    ],
  ],
  [
    'crunch',
    [
      'crunch',
      'sit-up',
      'sit up',
      'situp',
      'abdominal',
      'abdominais',
      'abdos',
      'encolhimento abdominal',
      '腹筋',
      'クランチ',
      'シットアップ',
      '卷腹',
      '仰卧起坐',
    ],
  ],
  ['plank', ['plank', 'plancha', 'prancha', 'planche', 'gainage', 'プランク', '平板支撑']],
  ['squat', ['squat', 'sentadilla', 'agachamento', 'wall sit', 'スクワット', '深蹲']],
  ['lunge', ['lunge', 'zancada', 'estocada', 'avanço', 'afundo', 'fente', 'ランジ', '弓步']],
  ['mountain', ['mountain', 'tadasana', 'montaña', 'montanha', 'montagne', '山のポーズ', '山式']],
  [
    'run',
    [
      'run',
      'jog',
      'sprint',
      'treadmill',
      'correr',
      'carrera',
      'trotar',
      'corrida',
      'courir',
      'course',
      'footing',
      'jogging',
      'ランニング',
      'ジョギング',
      '走る',
      '跑',
    ],
  ],
  [
    'cycle',
    [
      'cycl',
      'bike',
      'biking',
      'spinning',
      'bici',
      'ciclismo',
      'bicicleta',
      'pedal',
      'vélo',
      'velo',
      'cyclisme',
      'サイクリング',
      'バイク',
      '自転車',
      '骑行',
      '单车',
      '自行车',
    ],
  ],
  [
    'stretch',
    [
      'stretch',
      'mobility',
      'estira',
      'estiramiento',
      'alonga',
      'étire',
      'étirement',
      'etirement',
      'ストレッチ',
      '拉伸',
      '伸展',
    ],
  ],
  ['pose', ['pose', 'asana', 'postura', 'posture', 'ポーズ', '式']],
]

const MOVE_MATCHERS = MOVE_WORDS.map(([id, words]) => [id, matcher(words)] as const)

// Best guess for an item without a stored `move`. A held bridge is the yoga pose.
export function inferMove(name: string, kind?: 'sets' | 'timed' | 'distance'): MoveId {
  for (const [id, test] of MOVE_MATCHERS)
    if (test(name)) return id === 'bridge' && kind === 'timed' ? 'bridgepose' : id
  return 'generic'
}

export const moveOf = (i: { name: string; kind?: 'sets' | 'timed' | 'distance'; move?: string }) =>
  isMoveId(i.move) ? i.move : inferMove(i.name, i.kind)

export const isYogaMove = (m: MoveId) => (YOGA_MOVES as readonly string[]).includes(m)
